import { DateRange } from 'react-day-picker';
import {
  addMonths,
  subMonths,
  addDays,
  subDays,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
} from 'date-fns';
import { PeriodType } from '@/hooks/useTransactionFilters';

export const PERIOD_OPTIONS: Array<{ value: PeriodType; label: string }> = [
  { value: 'today', label: 'Hoje' },
  { value: 'thisweek', label: 'Esta semana' },
  { value: 'month', label: 'Este mês' },
  { value: 'year', label: 'Este ano' },
  { value: '30days', label: 'Últimos 30 dias' },
  { value: 'all', label: 'Todo o período' },
  { value: 'custom', label: 'Personalizado' },
];

export function handlePeriodSelect(
  period: PeriodType,
  now: Date
): DateRange | undefined {
  let newDateRange: DateRange | undefined;

  switch (period) {
    case 'today':
      newDateRange = { from: startOfDay(now), to: endOfDay(now) };
      break;
    case 'thisweek':
      newDateRange = { from: startOfWeek(now, { weekStartsOn: 0 }), to: endOfWeek(now, { weekStartsOn: 0 }) };
      break;
    case 'month':
      newDateRange = { from: startOfMonth(now), to: endOfMonth(now) };
      break;
    case 'year':
      newDateRange = { from: startOfYear(now), to: endOfYear(now) };
      break;
    case '30days':
      newDateRange = { from: subDays(now, 29), to: endOfDay(now) };
      break;
    case 'all':
      newDateRange = { from: undefined, to: undefined };
      break;
    default:
      return undefined;
  }

  return newDateRange;
}

export function handleNavigateMonth(
  direction: 'prev' | 'next',
  periodType: PeriodType,
  appliedDateRange: DateRange | { from?: Date; to?: Date } | undefined,
  currentDisplayDate: Date
): { newDateRange: DateRange; newPeriodType: PeriodType } {
  const baseDate = appliedDateRange?.from
    ? (appliedDateRange.from instanceof Date ? appliedDateRange.from : new Date(appliedDateRange.from))
    : currentDisplayDate;

  let newDateRange: DateRange;
  const offset = direction === 'prev' ? -1 : 1;

  switch (periodType) {
    case 'today':
      const newDay = addDays(baseDate, offset);
      newDateRange = {
        from: startOfDay(newDay),
        to: endOfDay(newDay),
      };
      return { newDateRange, newPeriodType: 'today' };

    case 'thisweek':
      const weekStart = startOfWeek(baseDate, { weekStartsOn: 0 });
      const newWeekStart = addDays(weekStart, offset * 7);
      newDateRange = {
        from: startOfWeek(newWeekStart, { weekStartsOn: 0 }),
        to: endOfWeek(newWeekStart, { weekStartsOn: 0 }),
      };
      return { newDateRange, newPeriodType: 'thisweek' };

    case 'month':
      const newMonth = addMonths(startOfMonth(baseDate), offset);
      newDateRange = {
        from: startOfMonth(newMonth),
        to: endOfMonth(newMonth),
      };
      return { newDateRange, newPeriodType: 'month' };

    case 'year':
      const newYear = addMonths(startOfYear(baseDate), offset * 12);
      newDateRange = {
        from: startOfYear(newYear),
        to: endOfYear(newYear),
      };
      return { newDateRange, newPeriodType: 'year' };

    case '30days':
      const newStart = addDays(baseDate, offset);
      newDateRange = {
        from: startOfDay(newStart),
        to: endOfDay(addDays(newStart, 29)),
      };
      return { newDateRange, newPeriodType: '30days' };

    case 'custom':
      const newCustomMonth = addMonths(startOfMonth(baseDate), offset);
      newDateRange = {
        from: startOfMonth(newCustomMonth),
        to: endOfMonth(newCustomMonth),
      };
      return { newDateRange, newPeriodType: 'custom' };

    default:
      const newFallbackMonth = addMonths(startOfMonth(baseDate), offset);
      newDateRange = {
        from: startOfMonth(newFallbackMonth),
        to: endOfMonth(newFallbackMonth),
      };
      return { newDateRange, newPeriodType: 'custom' };
  }
}
